import { Dropdown } from "primereact/dropdown";
import { SelectButton } from "primereact/selectbutton";
import { Tag } from "primereact/tag";
import { CatRunChangeResult } from "../../../client/types.gen";
import { CatRunStatusSeverity, catRunStatusSeverity } from "./staleResults";
import { ModelTabOption } from "./types";
import styles from "./CatCard.module.css";

type ModelOption = { label: string; value: string };

// A vendor's tab freshness, plus "running" during a run and "loading" while the
// precheck refetches — so a stale value isn't shown as current mid-flight.
export type ModelTabStatus = CatRunChangeResult | "running" | "loading";

type CatModelSwitcherProps = {
  selectedModel: ModelTabOption;
  onChange: (model: ModelTabOption) => void;
  isMobile: boolean;
  statusByModel?: Partial<Record<ModelTabOption, ModelTabStatus>>;
  showReplacementCost: boolean;
};

const STATUS_ICON: Record<
  CatRunStatusSeverity,
  { icon: string; label: string }
> = {
  fresh: { icon: "pi pi-check-circle", label: "Up to date" },
  warn: { icon: "pi pi-exclamation-circle", label: "Stale — re-run" },
  error: { icon: "pi pi-times-circle", label: "Out of date — re-run" },
  unrun: { icon: "pi pi-circle", label: "Not yet run" },
  unverified: { icon: "pi pi-question-circle", label: "Freshness unknown" },
};

// Color lives in CSS: severity ``fresh`` -> ``.tabStatusFresh``.
const statusColorClass = (severity: CatRunStatusSeverity): string =>
  styles[`tabStatus${severity.charAt(0).toUpperCase()}${severity.slice(1)}`] ??
  "";

/**
 * Model picker for the Modeling tab. A SelectButton group on desktop, a compact
 * Dropdown on phones — same options either way. Each option carries an
 * at-a-glance freshness icon when ``statusByModel`` has an entry for it.
 */
export const CatModelSwitcher = ({
  selectedModel,
  onChange,
  isMobile,
  statusByModel,
  showReplacementCost,
}: CatModelSwitcherProps) => {
  const options: ModelOption[] = [
    { label: "RMS", value: "rms_elt" },
    { label: "KatRisk", value: "spatialkat" },
    { label: "ICM", value: "icm_ylt" },
    { label: "Rater", value: "rater" },
    { label: "EB Rater", value: "eqbd" },
    ...(showReplacementCost
      ? [{ label: "Replacement Cost", value: "sindri" }]
      : []),
  ];

  const itemTemplate = (option: ModelOption) => {
    const status = statusByModel?.[option.value as ModelTabOption];
    const spinning = status === "running" || status === "loading";
    const severity =
      status && status !== "running" && status !== "loading"
        ? catRunStatusSeverity(status)
        : null;
    const icon = severity ? STATUS_ICON[severity] : null;
    return (
      <span className={styles.modelOption}>
        {option.label}
        {option.value === "icm_ylt" && (
          <Tag value="Beta" severity="info" className={styles.smallTag} />
        )}
        {spinning ? (
          <i
            className={`pi pi-spin pi-spinner ${styles.tabStatusIcon}`}
            title={status === "running" ? "Running…" : "Checking…"}
          />
        ) : (
          severity &&
          icon && (
            <i
              className={`pi ${icon.icon} ${styles.tabStatusIcon} ${statusColorClass(severity)}`}
              title={icon.label}
            />
          )
        )}
      </span>
    );
  };

  return isMobile ? (
    <Dropdown
      value={selectedModel}
      onChange={(e) => e.value && onChange(e.value)}
      options={options}
      optionLabel="label"
      optionValue="value"
      itemTemplate={itemTemplate}
      className={styles.modelDropdown}
    />
  ) : (
    <SelectButton
      value={selectedModel}
      onChange={(e) => e.value && onChange(e.value)}
      options={options}
      itemTemplate={itemTemplate}
    />
  );
};
